import * as React from 'react'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { OneAlert, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import {
  ImportProcessSchemaAdapterDocument,
  UniversalImporterImportProcessMetadataInput,
  UniversalImporterProcessDetailsDocument,
  UniversalImporterProcessDetailsQuery,
  UniversalImporterProcessDocument,
  UniversalImporterProcessQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

export const DEFAULT_COLUMN_VALUE = '-'

type UniversalImporterNuvoDropdownOption = {
  label: string
  value: string
}

interface ImportProcessDataCollectionProps {
  setSelectedRowColumns: (columns: Record<string, string>) => void
  setSelectedRowErrors: (errors: string) => void
  setIsModalOpen: (isOpen: boolean) => void
  setIsFixModalOpen: (isOpen: boolean) => void
  importProcessId: string
}

export const ImportProcessDataCollection = ({
  setSelectedRowColumns,
  setSelectedRowErrors,
  setIsModalOpen,
  setIsFixModalOpen,
  importProcessId,
}: ImportProcessDataCollectionProps) => {
  const { access: currentUser, company } = useCurrentSession()
  const companyId: number = company.id

  const [searchParams] = useSearchParams()
  const failedMessage = searchParams.get('failed_message')

  const parseColumnValue = (
    columnType: string,
    value: unknown,
    options: UniversalImporterNuvoDropdownOption[]
  ): string => {
    if (!value) return DEFAULT_COLUMN_VALUE

    let result: string
    switch (columnType) {
      case 'category':
        const option = options?.find((opt) => opt.value === value)
        result = option?.label ?? String(value)
        break
      case 'float':
        result = parseFloat(String(value)).toString()
        break
      case 'boolean':
        result = value ? i18n.t('common.yes') : i18n.t('common.no')
        break
      default:
        result = String(value)
    }

    return result.length > 30 ? `${result.slice(0, 30)}...` : result
  }

  const { data: importProcessData } = useQuery(UniversalImporterProcessDocument, {
    variables: { importProcessId: Number(importProcessId) },
  })

  type UniversalImporterImportProcessMetadata = NonNullable<
    NonNullable<
      NonNullable<UniversalImporterProcessQuery['universalImporter']>['importProcess']
    >['metadata']
  >[number]

  const importType = importProcessData?.universalImporter.importProcess?.importType ?? ''
  const legalEntityId = importProcessData?.universalImporter.importProcess?.legalEntity?.id
  const metadata: UniversalImporterImportProcessMetadata[] | undefined =
    importProcessData?.universalImporter.importProcess?.metadata || undefined

  const metadataInput: UniversalImporterImportProcessMetadataInput[] = useMemo(() => {
    if (metadata && metadata.length > 0) {
      return metadata.map((item: UniversalImporterImportProcessMetadata) => ({
        key: item.key,
        values: item.values,
        type: item.type,
      }))
    }

    if (legalEntityId) {
      return [
        {
          key: 'legal_entity_ids',
          values: [String(legalEntityId)],
          type: 'integer',
        },
      ]
    }

    return []
  }, [metadata, legalEntityId])

  const legalEntitiesIds = useMemo(() => {
    if (legalEntityId) {
      return [String(legalEntityId)]
    }
    return []
  }, [legalEntityId])

  const tracker = React.useMemo(
    () => ({
      id: crypto.randomUUID(),
      refresh() {
        this.id = crypto.randomUUID()
      },
    }),
    []
  )

  // Refresh tracker when importProcessId changes (new process = new session)
  React.useEffect(() => {
    tracker.refresh()
  }, [importProcessId, tracker])

  const commonEventProperties = useUnsafeMemo(
    () => ({
      user_id: currentUser.id,
      company_id: companyId,
      legal_entity_ids: legalEntitiesIds,
      import_type: importType,
      tracker_id: tracker.id,
    }),
    [currentUser.id, companyId, legalEntitiesIds, importType, tracker.id]
  )

  const { data: schemaAdapterData } = useQuery(ImportProcessSchemaAdapterDocument, {
    variables: {
      companyId: company.id,
      importType,
      metadata: metadataInput,
    },
  })

  const { data: dataFix } = useQuery(UniversalImporterProcessDocument, {
    variables: {
      importProcessId: Number(importProcessId),
    },
  })

  const importProcess = dataFix?.universalImporter.importProcess
  const failedProcessTreeProcesses =
    dataFix?.universalImporter.importProcessesConnection?.nodes ?? []
  const isFixable = failedProcessTreeProcesses.length === 0 && !importProcess?.outdated

  const source = useGraphqlDataCollectionSource({
    document: UniversalImporterProcessDetailsDocument,
    itemOnClick: (row) => () => {
      setIsModalOpen(true)
      setSelectedRowErrors(row._errors ?? '')
      const { _errors, _row_uuid, ...columns } = row
      const formattedData: Record<string, string> =
        schemaAdapterData?.universalImporter?.schemaAdaptersConnection?.nodes?.reduce(
          (acc, node) => ({
            ...acc,
            [node.label]: parseColumnValue(
              node.columnType ?? '',
              columns[node.key] ?? '',
              node.dropdownOptions ?? []
            ),
          }),
          {}
        ) ?? {}
      setSelectedRowColumns(formattedData)
    },
    search: { enabled: true },
    totalItemSummary: (totalItems) => `${totalItems} errors`,
    mapVariables: ({ search }) => ({
      importType,
      processId: Number(importProcessId),
      companyId: company.id,
      status: ['errored'],
      search,
    }),
    primaryActions: () =>
      isFixable
        ? {
            icon: Pencil,
            label: i18n.t('universal_importer.import_history_process.resource_header.fix'),
            onClick: () => {
              track(trackingEvents.universalImporter.imports.fixErrors, {
                total_failed_rows: importProcess?.errored ?? 0,
                total_rows: importProcess?.total ?? 0,
                ...commonEventProperties,
              })
              setIsFixModalOpen(true)
            },
          }
        : undefined,
    mapData: (data: UniversalImporterProcessDetailsQuery) => {
      const rowsData: Record<string, string>[] =
        data?.universalImporter?.importProcessRowsConnection?.edges?.flatMap(
          (edge) =>
            edge?.node?.rows?.map((row) => {
              const rowColumns: Record<string, string> =
                row?.columns?.reduce(
                  (acc, column) => ({
                    ...acc,
                    [column.key]: column.value,
                  }),
                  {}
                ) ?? {}

              rowColumns._errors = parseErrorMessages(row?.errors) ?? ''

              return rowColumns
            }) ?? []
        ) ?? []
      return rowsData
    },
    mapPagination: (data: UniversalImporterProcessDetailsQuery) => ({
      total: data.universalImporter?.importProcessRowsConnection?.totalCount ?? 0,
    }),
  })

  const dynamicColumns =
    schemaAdapterData?.universalImporter?.schemaAdaptersConnection?.nodes.slice(0, 3)

  return (
    <>
      {failedMessage && (
        <OneAlert
          title={i18n.t('universal_importer.import_history_process.failed_message.title')}
          description={i18n.t(
            'universal_importer.import_history_process.failed_message.description',
            {
              failedMessage,
            }
          )}
          variant="critical"
          action={{
            label: i18n.t('universal_importer.import_history_process.failed_message.action'),
            onClick: () => {},
          }}
        />
      )}
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t(
                    'universal_importer.import_history_process.data_collection.columns.errors'
                  ),
                  render: (_row) => {
                    const errorMessage = _row._errors || ''
                    return {
                      type: 'text',
                      value:
                        errorMessage.length > 40 ? `${errorMessage.slice(0, 40)}...` : errorMessage,
                    }
                  },
                },
                ...(dynamicColumns?.map((column) => ({
                  label: column.label,
                  render: (row: Record<string, string>) => ({
                    type: 'text' as const,
                    value: parseColumnValue(
                      column.columnType ?? '',
                      row[column.key] ?? '',
                      column.dropdownOptions ?? []
                    ),
                  }),
                })) ?? []),
              ],
              frozenColumns: 1,
            },
          },
        ]}
      />
    </>
  )
}

// Helper functions for error parsing
const parseErrorMessages = (errors: string[] | null | undefined): string => {
  if (!errors) return ''

  return errors.map((error) => processErrorString(error)).join(', ')
}

const processErrorString = (error: string): string => {
  let processed = error

  // Handle direct field structures like "{:factor=>["value"]}" first
  const directFieldMatch = processed.match(/^\{:(\w+)\s*=>\s*\[\\?"([^"\]\\]+)\\?"\]\}$/)
  if (
    directFieldMatch?.[1] &&
    !['messages', 'message', 'errors', 'error'].includes(directFieldMatch[1])
  ) {
    return `${directFieldMatch[1]}: ${directFieldMatch[2]}`
  }

  // Handle nested structures (single or multiple fields) FIRST
  processed = processed.replace(/\{([^}]+)\}/g, (match, innerContent) => {
    return parseNestedStructure(innerContent)
  })

  // Remove outer wrapper keys (messages, errors, message, error) - AFTER nested structure processing
  processed = processed.replace(/^\{:(?:messages?|errors?)\s*=>\s*\[/, '')
  processed = processed.replace(/\]\}$/, '')

  // Remove quotes around nested structures: "..." -> ...
  processed = processed.replace(/^"(.*)"$/, '$1')

  // Remove remaining quotes and brackets
  processed = processed.replace(/^["'\\]+|["'\\]+$/g, '')
  processed = processed.replace(/^\[|\]$/g, '')

  return processed
}

const parseNestedStructure = (innerContent: string): string => {
  const fields = splitFieldsByBrackets(innerContent)
  const formattedFields = fields.map((fieldPart) => formatFieldPart(fieldPart))
  return formattedFields.join(', ')
}

const splitFieldsByBrackets = (innerContent: string): string[] => {
  const fields: string[] = []
  let currentField = ''
  let bracketDepth = 0

  for (const char of innerContent) {
    if (char === '[') {
      bracketDepth += 1
    } else if (char === ']') {
      bracketDepth -= 1
    } else if (char === ',' && bracketDepth === 0) {
      fields.push(currentField.trim())
      currentField = ''
      continue
    }
    currentField += char
  }
  if (currentField.trim()) {
    fields.push(currentField.trim())
  }
  return fields
}

const formatFieldPart = (fieldPart: string): string => {
  const fieldMatch = fieldPart.match(/^:(\w+)\s*=>\s*\[(.+)\]$/)
  if (fieldMatch) {
    const fieldName = fieldMatch[1]
    const fieldContent = fieldMatch[2]
    const cleanedValue = cleanFieldContent(fieldContent || '')
    return `${fieldName}: ${cleanedValue}`
  }
  return fieldPart
}

const cleanFieldContent = (fieldContent: string): string => {
  return (fieldContent || '')
    .replace(/\\+["']/g, '"') // Replace escaped quotes with regular quotes
    .replace(/^["']|["']$/g, '') // Remove leading/trailing quotes
    .replace(/^\[|\]$/g, '') // Remove array brackets if present
    .split(/",\s*"/) // Split by comma to handle multiple values
    .map((s: string) => s.trim()) // Remove whitespace
    .join(', ') // Join multiple values with comma
}
